import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Play, Mic, MicOff, Square, Award, CheckCircle, 
  TrendingUp, Lightbulb, Target, AlertCircle, Volume2, 
  ThumbsUp, ThumbsDown, Clock, Gauge, MessageCircle, Zap,
  ChevronRight, X, BookOpen, Sparkles, HelpCircle, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { QualityTaskOutcome } from '../components/quality/QualityTaskOutcome';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// COACHING HINTS COMPONENT
// ============================================================================
const CoachingHint = ({ hint, hintType, isVisible, onDismiss }) => {
  if (!isVisible || !hint) return null;
  
  const typeStyles = {
    question: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', icon: HelpCircle, iconColor: 'text-blue-400' },
    empathy: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', icon: MessageCircle, iconColor: 'text-emerald-400' },
    warning: { bg: 'bg-amber-500/20', border: 'border-amber-500/40', icon: AlertCircle, iconColor: 'text-amber-400' },
    opportunity: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', icon: Sparkles, iconColor: 'text-purple-400' },
    tip: { bg: 'bg-cyan-500/20', border: 'border-cyan-500/40', icon: Lightbulb, iconColor: 'text-cyan-400' }
  };
  
  const style = typeStyles[hintType] || typeStyles.tip;
  const Icon = style.icon;
  
  return (
    <div 
      className={`${style.bg} ${style.border} border rounded-xl p-4 animate-in slide-in-from-right-5 duration-300`}
      data-testid="coaching-hint"
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-lg ${style.bg}`}>
          <Icon size={16} className={style.iconColor} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`text-xs font-medium uppercase tracking-wide ${style.iconColor}`}>
              Coach Hint
            </span>
            <button 
              onClick={onDismiss}
              className="text-gray-500 hover:text-gray-400 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
          <p className="text-sm text-gray-200 leading-relaxed">{hint}</p>
        </div>
      </div>
    </div>
  );
};

// Coaching Toggle Button
const CoachingToggle = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid="coaching-toggle"
    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
      enabled 
        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40' 
        : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
    }`}
    title={enabled ? "Coaching hints are ON - click to disable" : "Coaching hints are OFF - click to enable"}
  >
    {enabled ? <Eye size={16} /> : <EyeOff size={16} />}
    <span>Coach</span>
    <span className={`w-2 h-2 rounded-full ${enabled ? 'bg-purple-400' : 'bg-gray-500'}`} />
  </button>
);

// ============================================================================
// PRE-SCENARIO PREPARATION MODAL
// ============================================================================
const PreparationModal = ({ isOpen, onClose, onStart, preparation, scenarioName }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[hsl(240,10%,8%)] rounded-2xl border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[hsl(240,10%,8%)] p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <BookOpen size={24} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Before You Start</h2>
                <p className="text-sm text-gray-400">{scenarioName}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Summary */}
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-5 border border-blue-500/20">
            <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
              <Target size={18} />
              Scenario Summary
            </h3>
            <p className="text-gray-300 leading-relaxed">{preparation?.summary}</p>
          </div>
          
          {/* Why It Helps */}
          <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/20">
            <h3 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
              <Zap size={18} />
              Why This Helps
            </h3>
            <p className="text-gray-300 leading-relaxed">{preparation?.why_it_helps}</p>
          </div>
          
          {/* Do's and Don'ts */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Do's */}
            <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/20">
              <h3 className="font-semibold text-green-400 mb-3 flex items-center gap-2">
                <ThumbsUp size={18} />
                Best Practices
              </h3>
              <ul className="space-y-2">
                {preparation?.dos?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Don'ts */}
            <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/20">
              <h3 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                <ThumbsDown size={18} />
                Avoid These
              </h3>
              <ul className="space-y-2">
                {preparation?.donts?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <AlertCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Example Phrases */}
          <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/20">
            <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <MessageCircle size={18} />
              Example Phrases to Use
            </h3>
            <div className="space-y-2">
              {preparation?.example_phrases?.map((phrase, i) => (
                <div key={i} className="flex items-start gap-2 p-3 bg-black/30 rounded-lg">
                  <span className="text-amber-400 font-bold">{i + 1}.</span>
                  <p className="text-gray-300 text-sm italic">{phrase}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-[hsl(240,10%,8%)] p-6 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            data-testid="preparation-skip-btn"
            className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors text-gray-300"
          >
            Skip
          </button>
          <button
            onClick={onStart}
            data-testid="preparation-ready-btn"
            className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
          >
            <CheckCircle size={18} />
            Ready - Start Role-Play
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// METRIC GAUGE COMPONENT
// ============================================================================
const MetricGauge = ({ label, value, icon: Icon, colorClass, testId }) => (
  <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className={colorClass.text} />
      <span className="text-sm text-gray-400">{label}</span>
    </div>
    <div className="flex items-end gap-2">
      <span className={`text-2xl font-bold ${colorClass.text}`} data-testid={`${testId}-value`}>{value}</span>
      <span className="text-gray-500 text-sm mb-1">/100</span>
    </div>
    <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
      <div 
        className={`h-full ${colorClass.bg} transition-all duration-500`}
        style={{ width: `${value}%` }}
      />
    </div>
  </div>
);

// Color classes for metrics
const METRIC_COLORS = {
  blue: { text: 'text-blue-400', bg: 'bg-blue-500' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500' },
  amber: { text: 'text-amber-400', bg: 'bg-amber-500' },
  purple: { text: 'text-purple-400', bg: 'bg-purple-500' }
};

// ============================================================================
// SPEECH ANALYTICS PANEL (Right Side)
// ============================================================================
const SpeechAnalyticsPanel = ({ analytics, isRecording, coachingEnabled, currentHint, hintType, onDismissHint }) => {
  return (
    <div className="w-80 bg-[hsl(240,10%,6%)] border-l border-white/10 p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Gauge size={20} className="text-blue-400" />
        <h3 className="font-semibold text-white">Speech Analytics</h3>
      </div>
      
      {isRecording && (
        <div className="mb-4 p-3 bg-red-500/20 rounded-xl border border-red-500/30 flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-red-400 text-sm">Recording...</span>
        </div>
      )}
      
      {/* Coaching Hint - shows above metrics when enabled */}
      {coachingEnabled && currentHint && (
        <div className="mb-4">
          <CoachingHint 
            hint={currentHint} 
            hintType={hintType} 
            isVisible={true}
            onDismiss={onDismissHint}
          />
        </div>
      )}
      
      <div className="space-y-3 flex-1">
        <MetricGauge 
          label="Communication" 
          value={analytics?.communication_score || 0} 
          icon={MessageCircle}
          colorClass={METRIC_COLORS.blue}
          testId="metric-communication"
        />
        <MetricGauge 
          label="Professional Tone" 
          value={analytics?.professional_tone_score || 0} 
          icon={Award}
          colorClass={METRIC_COLORS.emerald}
          testId="metric-tone"
        />
        <MetricGauge 
          label="Clarity" 
          value={analytics?.clarity_score || 0} 
          icon={Target}
          colorClass={METRIC_COLORS.amber}
          testId="metric-clarity"
        />
        <MetricGauge 
          label="Goal Alignment" 
          value={analytics?.goal_alignment_score || 0} 
          icon={TrendingUp}
          colorClass={METRIC_COLORS.purple}
          testId="metric-goal"
        />
      </div>
      
      {/* Additional Stats */}
      <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Words/Min</span>
          <span className="text-white font-medium">{analytics?.words_per_minute || 0}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Total Words</span>
          <span className="text-white font-medium">{analytics?.total_words || 0}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Filler Words</span>
          <span className={`font-medium ${(analytics?.filler_words_count || 0) > 3 ? 'text-red-400' : 'text-green-400'}`}>
            {analytics?.filler_words_count || 0}
          </span>
        </div>
      </div>
      
      {/* Coaching Mode Indicator */}
      {coachingEnabled && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 text-xs text-purple-400">
            <Sparkles size={14} />
            <span>Coaching hints enabled</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COUNTDOWN OVERLAY
// ============================================================================
const CountdownOverlay = ({ seconds, onComplete }) => {
  const [count, setCount] = useState(seconds);
  
  useEffect(() => {
    if (count > 0) {
      const timer = setTimeout(() => setCount(count - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      onComplete();
    }
  }, [count, onComplete]);
  
  if (count === 0) return null;
  
  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20">
      <div className="text-center">
        <div className="text-8xl font-bold text-white mb-4 animate-pulse">{count}</div>
        <p className="text-gray-300">Get ready to speak...</p>
        <div className="flex items-center justify-center gap-2 mt-4 text-blue-400">
          <Mic size={24} />
          <span>Microphone will activate</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN VOICE ROLE-PLAY SESSION COMPONENT
// ============================================================================
export const VoiceRolePlaySession = () => {
  const { scenarioId } = useParams();
  const assignedTask = useSearchParams()[0].get('qt');
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState(null);
  const [preparation, setPreparation] = useState(null);
  const [showPreparation, setShowPreparation] = useState(true);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  useProctoredAttempt({ active: Boolean(session) && !evaluation && !showPreparation, label: 'role-play', onForceEnd: () => endSession() });
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [silenceTimer, setSilenceTimer] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [waitingForContinue, setWaitingForContinue] = useState(false); // Wait for user to click Continue
  
  // Speech analytics
  const [currentAnalytics, setCurrentAnalytics] = useState(null);
  const [aggregateAnalytics, setAggregateAnalytics] = useState({
    communication_score: 0,
    professional_tone_score: 0,
    clarity_score: 0,
    goal_alignment_score: 0,
    words_per_minute: 0,
    total_words: 0,
    filler_words_count: 0
  });
  
  // Coaching hints state (disabled by default)
  const [coachingEnabled, setCoachingEnabled] = useState(false);
  const [currentHint, setCurrentHint] = useState(null);
  const [hintType, setHintType] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const silenceDetectorRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sessionRef = useRef(null); // Keep track of session for callbacks
  
  // Update sessionRef when session changes
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  
  // Fetch scenario and preparation
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [scenarioRes, prepRes] = await Promise.all([
          axios.get(`${API_URL}/api/roleplay/scenarios/${scenarioId}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/roleplay/scenario/${scenarioId}/preparation`, { withCredentials: true })
        ]);
        setScenario(scenarioRes.data);
        setPreparation(prepRes.data.preparation);
      } catch (error) {
        console.error('Error fetching scenario:', error);
        navigate('/roleplay');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    return () => {
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [scenarioId, navigate]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Start session
  const startSession = async () => {
    try {
      setLoading(true);
      
      // Check microphone access FIRST - before starting the session
      const micInitialized = await initMicrophone();
      if (!micInitialized) {
        setLoading(false);
        return; // Don't proceed if mic isn't available
      }
      
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/start?scenario_id=${scenarioId}`,
        {},
        { withCredentials: true }
      );
      setSession({
        session_id: res.data.session_id,
        scenario: res.data.scenario,
        persona: res.data.persona
      });
      setMessages([{
        role: 'assistant',
        content: res.data.opening_message,
        persona_name: res.data.persona?.name || scenario?.ai_persona?.name
      }]);
      setShowPreparation(false);
      
      // Wait for user to click Continue before first recording
      // This gives them time to read the AI's opening message
      setWaitingForContinue(true);
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initialize microphone
  const initMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      streamRef.current = stream;
      
      // Setup audio context for silence detection
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      return true;
    } catch (error) {
      console.error('Microphone error:', error);
      alert('Please allow microphone access to use voice role-play.');
      return false;
    }
  };
  
  // Start recording
  const startRecording = useCallback(async () => {
    if (!streamRef.current) {
      const initialized = await initMicrophone();
      if (!initialized) return;
    }
    
    audioChunksRef.current = [];
    setRecordingDuration(0);
    
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType: 'audio/webm;codecs=opus'
    });
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };
    
    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      await processVoiceMessage(audioBlob);
    };
    
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(100);
    setIsRecording(true);
    
    // Recording duration timer
    recordingTimerRef.current = setInterval(() => {
      setRecordingDuration(prev => {
        const newDuration = prev + 1;
        // Max 60 seconds
        if (newDuration >= 60) {
          stopRecording();
        }
        return newDuration;
      });
    }, 1000);
    
    // Silence detection
    startSilenceDetection();
  }, []);
  
  // Silence detection
  const startSilenceDetection = () => {
    let silenceStart = null;
    
    const checkSilence = () => {
      if (!analyserRef.current || !isRecording) return;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      
      if (average < 10) {
        // Silence detected
        if (!silenceStart) {
          silenceStart = Date.now();
        } else if (Date.now() - silenceStart > 10000) {
          // 10 seconds of silence
          stopRecording();
          return;
        }
      } else {
        silenceStart = null;
      }
      
      silenceDetectorRef.current = requestAnimationFrame(checkSilence);
    };
    
    silenceDetectorRef.current = requestAnimationFrame(checkSilence);
  };
  
  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    if (silenceDetectorRef.current) {
      cancelAnimationFrame(silenceDetectorRef.current);
    }
  }, []);
  
  // Process voice message
  const processVoiceMessage = async (audioBlob) => {
    setProcessing(true);
    setCurrentHint(null); // Clear previous hint
    
    // Use sessionRef to get the current session (avoids stale closure)
    const currentSession = sessionRef.current;
    
    console.log('Processing voice message, blob size:', audioBlob.size, 'type:', audioBlob.type);
    console.log('Current session:', currentSession?.session_id);
    
    if (!currentSession || !currentSession.session_id) {
      alert('Session expired. Please start a new role-play session.');
      setProcessing(false);
      return;
    }
    
    if (!audioBlob || audioBlob.size < 1000) {
      alert('Recording too short. Please speak for at least a few seconds.');
      setTimeout(() => setShowCountdown(true), 1000);
      setProcessing(false);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      console.log('Sending to:', `${API_URL}/api/roleplay/sessions/${currentSession.session_id}/voice-message`);
      
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/${currentSession.session_id}/voice-message`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 second timeout
        }
      );
      
      console.log('Response:', res.data);
      
      if (res.data.success) {
        // Add user message
        setMessages(prev => [...prev, {
          role: 'user',
          content: res.data.transcript,
          speech_analytics: res.data.speech_analytics
        }]);
        
        // Update analytics
        if (res.data.speech_analytics) {
          setCurrentAnalytics(res.data.speech_analytics);
          updateAggregateAnalytics(res.data.speech_analytics);
        }
        
        // Add AI response
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res.data.ai_response,
          persona_name: scenario?.ai_persona?.name
        }]);
        
        // Fetch coaching hint if enabled
        if (coachingEnabled) {
          fetchCoachingHint();
        }
        
        // Show "Continue" button instead of auto-starting countdown
        // This gives the recruiter time to read the AI response
        setWaitingForContinue(true);
      } else {
        const errorMsg = res.data.error || 'Could not process your voice. Please try again.';
        console.error('Voice processing returned error:', res.data);
        alert(errorMsg);
        // Restart recording
        setTimeout(() => setShowCountdown(true), 1000);
      }
    } catch (error) {
      console.error('Voice processing error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      let errorDetail = 'Unknown error';
      if (error.response?.data?.detail) {
        errorDetail = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorDetail = error.response.data.error;
      } else if (error.message) {
        errorDetail = error.message;
      }
      
      alert(`Voice processing failed: ${errorDetail}\n\nPlease ensure:\n• Your microphone is working\n• You spoke clearly\n• Background noise is minimal`);
      setTimeout(() => setShowCountdown(true), 1000);
    } finally {
      setProcessing(false);
    }
  };
  
  // Fetch coaching hint from backend
  const fetchCoachingHint = useCallback(async () => {
    const currentSession = sessionRef.current;
    console.log('Fetching coaching hint...', { sessionId: currentSession?.session_id });
    
    if (!currentSession?.session_id) {
      console.log('No session, skipping hint fetch');
      return;
    }
    
    setHintLoading(true);
    try {
      console.log('Making API call to:', `${API_URL}/api/roleplay/sessions/${currentSession.session_id}/coaching-hint`);
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/${currentSession.session_id}/coaching-hint`,
        {},
        { withCredentials: true }
      );
      
      console.log('Coaching hint response:', res.data);
      
      if (res.data.hint) {
        setCurrentHint(res.data.hint);
        setHintType(res.data.hint_type || 'tip');
        console.log('Hint set successfully:', res.data.hint);
      } else {
        console.log('No hint returned from API');
      }
    } catch (error) {
      console.error('Error fetching coaching hint:', error);
    } finally {
      setHintLoading(false);
    }
  }, []);
  
  // Dismiss coaching hint
  const dismissHint = () => {
    setCurrentHint(null);
    setHintType(null);
  };
  
  // Toggle coaching mode
  const toggleCoaching = useCallback(() => {
    setCoachingEnabled(prev => {
      const newValue = !prev;
      console.log('Coaching toggled:', newValue);
      
      if (!newValue) {
        // Disabling coaching - clear hints
        setCurrentHint(null);
        setHintType(null);
      }
      
      return newValue;
    });
  }, []);
  
  // Effect to fetch hint when coaching is enabled
  useEffect(() => {
    if (coachingEnabled && sessionRef.current?.session_id) {
      console.log('Coaching enabled and session exists, fetching hint...');
      fetchCoachingHint();
    }
  }, [coachingEnabled, fetchCoachingHint]);
  
  // Handle Continue button - user is ready to respond
  const handleContinue = () => {
    setWaitingForContinue(false);
    setShowCountdown(true);
  };

  // Update aggregate analytics
  const updateAggregateAnalytics = (newAnalytics) => {
    setAggregateAnalytics(prev => {
      const count = messages.filter(m => m.role === 'user').length + 1;
      return {
        communication_score: Math.round((prev.communication_score * (count - 1) + newAnalytics.communication_score) / count),
        professional_tone_score: Math.round((prev.professional_tone_score * (count - 1) + newAnalytics.professional_tone_score) / count),
        clarity_score: Math.round((prev.clarity_score * (count - 1) + newAnalytics.clarity_score) / count),
        goal_alignment_score: Math.round((prev.goal_alignment_score * (count - 1) + newAnalytics.goal_alignment_score) / count),
        words_per_minute: Math.round((prev.words_per_minute * (count - 1) + newAnalytics.words_per_minute) / count),
        total_words: prev.total_words + newAnalytics.total_words,
        filler_words_count: prev.filler_words_count + newAnalytics.filler_words_count
      };
    });
  };
  
  // End session
  const endSession = async () => {
    const currentSession = sessionRef.current;
    if (!currentSession?.session_id) {
      alert('No active session to end.');
      return;
    }
    
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/${currentSession.session_id}/end`,
        {},
        { withCredentials: true }
      );
      setEvaluation({
        ...res.data.evaluation,
        speech_analytics: aggregateAnalytics
      });
      
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    } catch (error) {
      console.error('Error ending session:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle countdown complete
  const handleCountdownComplete = useCallback(() => {
    setShowCountdown(false);
    startRecording();
  }, [startRecording]);
  
  // Loading state
  if (loading && !scenario) {
    return (
      <div className="min-h-screen bg-[hsl(240,10%,4%)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Evaluation view
  if (evaluation) {
    const tryAgain = () => {
      setEvaluation(null);
      setSession(null);
      setMessages([]);
      setShowPreparation(true);
      setAggregateAnalytics({
        communication_score: 0,
        professional_tone_score: 0,
        clarity_score: 0,
        goal_alignment_score: 0,
        words_per_minute: 0,
        total_words: 0,
        filler_words_count: 0
      });
    };
    return (
      <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 mb-4">
              <Award size={40} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Session Complete!</h1>
            <p className="text-gray-400">{scenario?.name}</p>
          </div>
          
          {/* Overall Score */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center mb-6">
            <div className="text-gray-400 mb-2">Overall Score</div>
            <div className="text-5xl font-bold text-blue-400">{evaluation.overall_score}%</div>
          </div>
          
          {/* Speech Analytics Summary */}
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-6 border border-blue-500/20 mb-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Gauge size={20} className="text-blue-400" />
              Voice Communication Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400">{evaluation.speech_analytics?.communication_score || 0}%</div>
                <div className="text-sm text-gray-400">Communication</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{evaluation.speech_analytics?.professional_tone_score || 0}%</div>
                <div className="text-sm text-gray-400">Tone</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400">{evaluation.speech_analytics?.clarity_score || 0}%</div>
                <div className="text-sm text-gray-400">Clarity</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{evaluation.speech_analytics?.words_per_minute || 0}</div>
                <div className="text-sm text-gray-400">Words/Min</div>
              </div>
            </div>
          </div>
          
          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/20">
              <h3 className="flex items-center gap-2 font-semibold text-emerald-400 mb-3">
                <CheckCircle size={18} />
                What You Did Well
              </h3>
              <ul className="space-y-2">
                {evaluation.strengths?.map((strength, i) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-emerald-400 mt-0.5">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/20">
              <h3 className="flex items-center gap-2 font-semibold text-amber-400 mb-3">
                <TrendingUp size={18} />
                Areas to Improve
              </h3>
              <ul className="space-y-2">
                {evaluation.improvements?.map((improvement, i) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    {improvement}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Coaching Tips */}
          {evaluation.coaching_tips && evaluation.coaching_tips.length > 0 && (
            <div className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/20 mb-6">
              <h3 className="flex items-center gap-2 font-semibold text-blue-400 mb-3">
                <Lightbulb size={18} />
                Coaching Tips
              </h3>
              <ul className="space-y-2">
                {evaluation.coaching_tips.map((tip, i) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">{i + 1}.</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Actions */}
          <QualityTaskOutcome kind="roleplay" refKey={scenarioId} attemptScore={evaluation.overall_score} onRetake={tryAgain}>
          <div className="flex gap-4">
            <button
              onClick={tryAgain}
              data-testid="try-again-btn"
              className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl font-medium transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/roleplay')}
              data-testid="back-to-lab-btn"
              className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors"
            >
              Back to Lab
            </button>
          </div>
          </QualityTaskOutcome>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white flex flex-col">
      {/* Preparation Modal */}
      <PreparationModal
        isOpen={showPreparation && !session}
        onClose={() => setShowPreparation(false)}
        onStart={startSession}
        preparation={preparation}
        scenarioName={scenario?.name}
      />
      
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!assignedTask && (
              <button 
                onClick={() => navigate('/roleplay')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="roleplay-back-btn"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold">{scenario?.name}</h1>
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                  <Mic size={12} />
                  Voice Mode
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{scenario?.ai_persona?.name}</span>
                <span>•</span>
                <span>{scenario?.ai_persona?.title}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Coaching Toggle - only show when session is active */}
            {session && (
              <CoachingToggle enabled={coachingEnabled} onToggle={toggleCoaching} />
            )}
            
            {session && messages.filter(m => m.role === 'user').length >= 3 && (
              <button
                onClick={endSession}
                data-testid="end-session-btn"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors"
                disabled={loading || isRecording || processing}
              >
                {loading ? 'Evaluating...' : 'End & Get Feedback'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {!session ? (
            // Pre-session view
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="max-w-md text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-3xl font-bold">
                  {scenario?.ai_persona?.name?.charAt(0)}
                </div>
                <h2 className="text-xl font-semibold mb-2">{scenario?.ai_persona?.name}</h2>
                <p className="text-gray-400 mb-6">{scenario?.ai_persona?.title} at {scenario?.ai_persona?.company}</p>
                <button
                  onClick={() => setShowPreparation(true)}
                  data-testid="start-voice-roleplay-btn"
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Mic size={20} />
                      Start Voice Role-Play
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            // Chat view
            <div className="flex-1 flex flex-col relative">
              {/* Countdown Overlay */}
              {showCountdown && (
                <CountdownOverlay seconds={5} onComplete={handleCountdownComplete} />
              )}
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-lg ${
                      msg.role === 'user' 
                        ? 'bg-blue-500/20 border-blue-500/30' 
                        : 'bg-white/5 border-white/10'
                    } rounded-xl p-4 border`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold">
                            {msg.persona_name?.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-400">{msg.persona_name}</span>
                        </div>
                      )}
                      <p className="text-gray-200">{msg.content}</p>
                      {msg.speech_analytics && (
                        <div className="mt-2 pt-2 border-t border-white/10 flex gap-3 text-xs text-gray-500">
                          <span>{msg.speech_analytics.words_per_minute} WPM</span>
                          <span>{msg.speech_analytics.total_words} words</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Recording Status Bar */}
              <div className="p-4 border-t border-white/10 bg-white/5">
                <div className="max-w-4xl mx-auto">
                  {isRecording ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                        <span className="text-red-400">Recording... ({recordingDuration}s / 60s max)</span>
                      </div>
                      <button
                        onClick={stopRecording}
                        data-testid="stop-recording-btn"
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <Square size={16} />
                        Stop Recording
                      </button>
                    </div>
                  ) : processing ? (
                    <div className="flex items-center justify-center gap-3 text-blue-400">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                      Processing your response...
                    </div>
                  ) : waitingForContinue ? (
                    <div className="flex items-center justify-center gap-4">
                      <p className="text-gray-300">Read the response above, then click Continue when ready</p>
                      <button
                        onClick={handleContinue}
                        data-testid="continue-btn"
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-lg font-semibold transition-all flex items-center gap-2"
                      >
                        <Play size={16} />
                        Continue
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <p>Waiting for your response...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Speech Analytics Panel (Right Side) */}
        {session && (
          <SpeechAnalyticsPanel 
            analytics={currentAnalytics || aggregateAnalytics} 
            isRecording={isRecording}
            coachingEnabled={coachingEnabled}
            currentHint={currentHint}
            hintType={hintType}
            onDismissHint={dismissHint}
          />
        )}
      </div>
    </div>
  );
};

export default VoiceRolePlaySession;
